import { existsSync } from "node:fs";
import fs from "node:fs/promises";
import path from "node:path";
import type { BackendStatus, InstallResult, UninstallResult } from "./types.ts";

/**
 * Install the skaile integration for OMP.
 *
 * Always generates `.omp/extensions/skaile.ts` via `buildOmpExtensionSource()`
 * — there is no npm-package path. The only runtime dependency of the
 * generated extension is `@skaile/workspaces`.
 */
export async function install(projectDir: string): Promise<InstallResult> {
  const extDir = path.join(projectDir, ".omp", "extensions");
  const extPath = path.join(extDir, "skaile.ts");
  await fs.mkdir(extDir, { recursive: true });

  const { WorkspacePlugin } = await import("@skaile/workspaces/workspace-plugin");
  const plugin = new WorkspacePlugin({ projectDir });
  const nextSrc = await plugin.buildOmpExtensionSource();

  let prevSrc: string | null = null;
  try {
    prevSrc = await fs.readFile(extPath, "utf-8");
  } catch {
    /* file doesn't exist yet */
  }

  if (prevSrc === nextSrc) return { changed: false, method: "generated" };
  await fs.writeFile(extPath, nextSrc);
  return { changed: true, method: "generated" };
}

export async function uninstall(projectDir: string): Promise<UninstallResult> {
  const extPath = path.join(projectDir, ".omp", "extensions", "skaile.ts");
  try {
    await fs.unlink(extPath);
    return { changed: true };
  } catch {
    return { changed: false };
  }
}

export async function status(projectDir: string): Promise<BackendStatus> {
  const extPath = path.join(projectDir, ".omp", "extensions", "skaile.ts");
  if (existsSync(extPath)) {
    return {
      backend: "omp",
      installed: "yes",
      enabled: "n/a",
      method: "extension",
      location: path.relative(projectDir, extPath),
    };
  }
  return {
    backend: "omp",
    installed: "no",
    enabled: "n/a",
    method: "—",
    location: "—",
  };
}
